import React, { useState } from 'react';
import { X, MapPin, DollarSign, Clock, AlertTriangle, Hash } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card } from '../components/ui/card';
import axios from 'axios';
import { getAuthHeaders } from '../utils/auth';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CreateJobModal = ({ isOpen, onClose, onJobCreated, drivers }) => {
  const [formData, setFormData] = useState({
    custom_job_number: '',
    use_custom_job_number: false,
    tracking_number: '',
    pod_reference: '',
    pickup_address: '',
    pickup_city: '',
    pickup_state: '',
    pickup_zip: '',
    pickup_lat: 45.5152,
    pickup_lng: -122.6784,
    pickup_contact_name: '',
    pickup_contact_phone: '',
    pickup_date: '',
    pickup_time: '',
    delivery_address: '',
    delivery_city: '',
    delivery_state: '',
    delivery_zip: '',
    delivery_lat: 45.5231,
    delivery_lng: -122.6765,
    delivery_contact_name: '',
    delivery_contact_phone: '',
    deadline_date: '',
    deadline_time: '',
    pay_amount: '',
    special_instructions: '',
    priority: 'normal',
    assignment_type: 'offer',
    assign_to_driver: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Create job
      const jobData = {
        custom_job_number: formData.use_custom_job_number ? formData.custom_job_number : null,
        tracking_number: formData.tracking_number || null,
        pod_reference: formData.pod_reference || null,
        pickup_location: {
          address: formData.pickup_address,
          city: formData.pickup_city,
          state: formData.pickup_state,
          zip_code: formData.pickup_zip,
          latitude: parseFloat(formData.pickup_lat),
          longitude: parseFloat(formData.pickup_lng),
          contact_name: formData.pickup_contact_name,
          contact_phone: formData.pickup_contact_phone
        },
        delivery_location: {
          address: formData.delivery_address,
          city: formData.delivery_city,
          state: formData.delivery_state,
          zip_code: formData.delivery_zip,
          latitude: parseFloat(formData.delivery_lat),
          longitude: parseFloat(formData.delivery_lng),
          contact_name: formData.delivery_contact_name,
          contact_phone: formData.delivery_contact_phone
        },
        pay_amount: parseFloat(formData.pay_amount),
        pickup_datetime: formData.pickup_date && formData.pickup_time ? `${formData.pickup_date}T${formData.pickup_time}` : null,
        deadline_datetime: formData.deadline_date && formData.deadline_time ? `${formData.deadline_date}T${formData.deadline_time}` : null,
        special_instructions: formData.special_instructions,
        priority: formData.priority,
        assignment_type: formData.assignment_type
      };

      const response = await axios.post(
        `${BACKEND_URL}/api/dispatch/jobs/v2`,
        jobData,
        { headers: getAuthHeaders() }
      );

      // If driver assigned, offer job to them
      if (formData.assign_to_driver) {
        if (formData.assignment_type === 'assign') {
          // Direct assignment - no timer, immediately accepted
          await axios.post(
            `${BACKEND_URL}/api/dispatch/jobs/assign-direct`,
            {
              job_id: response.data.job.id,
              driver_id: formData.assign_to_driver
            },
            { headers: getAuthHeaders() }
          );
        } else {
          // Offer with timer
          await axios.post(
            `${BACKEND_URL}/api/dispatch/jobs/offer`,
            {
              job_id: response.data.job.id,
              driver_id: formData.assign_to_driver
            },
            { headers: getAuthHeaders() }
          );
        }
      }

      onJobCreated();
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create job');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <Card className="w-full max-w-4xl my-8">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-slate-900">Create New Job</h2>
            <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg">
              <X className="h-6 w-6" />
            </button>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Job Number */}
            <div>
              <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center">
                <Hash className="h-5 w-5 text-slate-600 mr-2" />
                Job Number
              </h3>
              <div className="flex items-center space-x-4 mb-3">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="radio"
                    name="job_number_type"
                    checked={!formData.use_custom_job_number}
                    onChange={() => setFormData({...formData, use_custom_job_number: false, custom_job_number: ''})}
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="text-sm text-slate-700">Auto-generate</span>
                </label>
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="radio"
                    name="job_number_type"
                    checked={formData.use_custom_job_number}
                    onChange={() => setFormData({...formData, use_custom_job_number: true})}
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="text-sm text-slate-700">Custom job number</span>
                </label>
              </div>
              {formData.use_custom_job_number && (
                <Input
                  value={formData.custom_job_number}
                  onChange={(e) => setFormData({...formData, custom_job_number: e.target.value.toUpperCase()})}
                  placeholder="Enter custom job number"
                  className="max-w-xs"
                  required={formData.use_custom_job_number}
                />
              )}
            </div>

            {/* Tracking Information */}
            <div>
              <h3 className="text-lg font-semibold text-slate-900 mb-4">Tracking Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Tracking Number</label>
                  <Input
                    value={formData.tracking_number}
                    onChange={(e) => setFormData({...formData, tracking_number: e.target.value})}
                    placeholder="VL1437-AO8239"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">POD Reference #</label>
                  <Input
                    value={formData.pod_reference}
                    onChange={(e) => setFormData({...formData, pod_reference: e.target.value})}
                    placeholder="G6935031"
                  />
                </div>
              </div>
            </div>

            {/* Pickup Location */}
            <div>
              <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center">
                <MapPin className="h-5 w-5 text-green-600 mr-2" />
                Pickup Location
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-1">Street Address *</label>
                  <Input
                    required
                    value={formData.pickup_address}
                    onChange={(e) => setFormData({...formData, pickup_address: e.target.value})}
                    placeholder="123 Main St"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">City *</label>
                  <Input
                    required
                    value={formData.pickup_city}
                    onChange={(e) => setFormData({...formData, pickup_city: e.target.value})}
                    placeholder="Tukwila"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">State *</label>
                  <Input
                    required
                    value={formData.pickup_state}
                    onChange={(e) => setFormData({...formData, pickup_state: e.target.value})}
                    placeholder="OR"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">ZIP Code *</label>
                  <Input
                    required
                    value={formData.pickup_zip}
                    onChange={(e) => setFormData({...formData, pickup_zip: e.target.value})}
                    placeholder="98188"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Contact Name</label>
                  <Input
                    value={formData.pickup_contact_name}
                    onChange={(e) => setFormData({...formData, pickup_contact_name: e.target.value})}
                    placeholder="John Doe"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Contact Phone</label>
                  <Input
                    value={formData.pickup_contact_phone}
                    onChange={(e) => setFormData({...formData, pickup_contact_phone: e.target.value})}
                    placeholder="(503) 555-0100"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Pickup Date *</label>
                  <Input
                    required
                    type="date"
                    value={formData.pickup_date}
                    onChange={(e) => setFormData({...formData, pickup_date: e.target.value})}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Pickup Time *</label>
                  <Input
                    required
                    type="time"
                    value={formData.pickup_time}
                    onChange={(e) => setFormData({...formData, pickup_time: e.target.value})}
                  />
                </div>
              </div>
            </div>

            {/* Delivery Location */}
            <div className="pt-6 border-t border-slate-200">
              <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center">
                <MapPin className="h-5 w-5 text-red-600 mr-2" />
                Delivery Location
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-1">Street Address *</label>
                  <Input
                    required
                    value={formData.delivery_address}
                    onChange={(e) => setFormData({...formData, delivery_address: e.target.value})}
                    placeholder="456 Oak Ave"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">City *</label>
                  <Input
                    required
                    value={formData.delivery_city}
                    onChange={(e) => setFormData({...formData, delivery_city: e.target.value})}
                    placeholder="Seattle"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">State *</label>
                  <Input
                    required
                    value={formData.delivery_state}
                    onChange={(e) => setFormData({...formData, delivery_state: e.target.value})}
                    placeholder="WA"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">ZIP Code *</label>
                  <Input
                    required
                    value={formData.delivery_zip}
                    onChange={(e) => setFormData({...formData, delivery_zip: e.target.value})}
                    placeholder="98101"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Contact Name</label>
                  <Input
                    value={formData.delivery_contact_name}
                    onChange={(e) => setFormData({...formData, delivery_contact_name: e.target.value})}
                    placeholder="Jane Smith"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Contact Phone</label>
                  <Input
                    value={formData.delivery_contact_phone}
                    onChange={(e) => setFormData({...formData, delivery_contact_phone: e.target.value})}
                    placeholder="(206) 555-0200"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Deadline Date *</label>
                  <Input
                    required
                    type="date"
                    value={formData.deadline_date}
                    onChange={(e) => setFormData({...formData, deadline_date: e.target.value})}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Deadline Time *</label>
                  <Input
                    required
                    type="time"
                    value={formData.deadline_time}
                    onChange={(e) => setFormData({...formData, deadline_time: e.target.value})}
                  />
                </div>
              </div>
            </div>

            {/* Job Details */}
            <div className="pt-6 border-t border-slate-200">
              <h3 className="text-lg font-semibold text-slate-900 mb-4">Job Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1 flex items-center">
                    <DollarSign className="h-4 w-4 mr-1" />
                    Pay Amount *
                  </label>
                  <Input
                    required
                    type="number"
                    step="0.01"
                    value={formData.pay_amount}
                    onChange={(e) => setFormData({...formData, pay_amount: e.target.value})}
                    placeholder="150.00"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1 flex items-center">
                    <AlertTriangle className="h-4 w-4 mr-1" />
                    Priority
                  </label>
                  <select
                    value={formData.priority}
                    onChange={(e) => setFormData({...formData, priority: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                  >
                    <option value="normal">Normal</option>
                    <option value="urgent">Urgent</option>
                    <option value="critical">Critical</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Assignment */}
            <div className="pt-6 border-t border-slate-200">
              <h3 className="text-lg font-semibold text-slate-900 mb-4">Assignment</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Assignment Type</label>
                  <select
                    value={formData.assignment_type}
                    onChange={(e) => setFormData({...formData, assignment_type: e.target.value})}
                    className="w-full h-10 px-3 rounded-md border border-slate-300 bg-white"
                  >
                    <option value="offer">Offer to Driver (15 sec timer)</option>
                    <option value="assign">Direct Assignment (No timer)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    {formData.assignment_type === 'assign' ? 'Assign to Driver *' : 'Offer to Driver (Optional)'}
                  </label>
                  <select
                    value={formData.assign_to_driver}
                    onChange={(e) => setFormData({...formData, assign_to_driver: e.target.value})}
                    className="w-full h-10 px-3 rounded-md border border-slate-300 bg-white"
                    required={formData.assignment_type === 'assign'}
                  >
                    <option value="">-- Select Driver --</option>
                    {drivers.filter(d => d.is_available).map(driver => (
                      <option key={driver.id} value={driver.id}>
                        {driver.full_name} ({driver.vehicle_type} - {driver.vehicle_number})
                      </option>
                    ))}
                  </select>
                </div>
                <div className="md:col-span-3">
                  <label className="block text-sm font-medium text-slate-700 mb-1">Special Instructions</label>
                  <Textarea
                    value={formData.special_instructions}
                    onChange={(e) => setFormData({...formData, special_instructions: e.target.value})}
                    rows={3}
                    placeholder="Any special handling requirements, gate codes, etc."
                  />
                </div>
              </div>
            </div>

            <div className="flex space-x-3 pt-4">
              <Button
                type="button"
                onClick={onClose}
                variant="outline"
                className="flex-1"
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-slate-800 hover:bg-slate-700"
                disabled={loading}
              >
                {loading ? 'Creating...' : 'Create Job'}
              </Button>
            </div>
          </form>
        </div>
      </Card>
    </div>
  );
};

export default CreateJobModal;